import React from 'react';
import { formatIntDateAndThenTime } from '../../../helpers/formatInDateAndThenTime';
import MediaGallery from './MediaGallery';
import UserMediaRow from './UserMediaRow';
import { getProjectLogs } from '../../../apis/impact-report/report';
import useFetchData from '../../../hooks/useFetchData';
import Loader from '../../../ui/Loader';
import BountyDescription from '../../bounties/BountyDescription';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatNumber } from '../../../helpers/formatNumber';
import Status from '../../../ui/Status';

/**
 * ProjectActivityLog Component
 * 
 * A vertical timeline component that displays project activities with media content.
 * 
 * @example
 * ```jsx
 * const activities = [
 *   {
 *     id: 1,
 *     type: 'Activity creation',
 *     title: "'Setup community solar panels' created",
 *     description: "The activity is about planning and executing on Gitcoin missions on Radio during GG20",
 *     date: '2024-02-01T00:00:00Z',
 *     media: []
 *   },
 *   {
 *     id: 2,
 *     type: 'Activity validation',
 *     title: "Funds worth $500 deployed",
 *     description: "80 submissions were validated, 40 Impact certificates were awarded & 4 claims rewarded",
 *     date: '2024-01-29T00:00:00Z',
 *     media: [
 *       {
 *         type: 'video',
 *         url: 'video-url',
 *         thumbnail: 'thumbnail-url',
 *         username: 'changemaker001',
 *         impactPoints: '3000',
 *         caption: 'Taken at 12:00 pm GMT +5:30, 22 Mar 2025, Indirangar Be...'
 *       },
 *       {
 *         type: 'image',
 *         url: 'image-url',
 *         username: 'tinawest12',
 *         impactPoints: '2000',
 *         caption: 'Community garden work'
 *       }
 *     ]
 *   }
 * ];
 * 
 * <ProjectActivityLog activities={activities} />
 * ```
 */
const activityTypeProjectLog = {
    "BOUNTY_CREATION": "Activity creation",
    "BOUNTY_EXPIRATION": "Activity particpation",
    "BOUNTY_VALIDATION": "Activity validation",


}


const ProjectActivityLog = ({ projectId, activities = [], imageGenerationTimestamp }) => {

    const { loading, response, error } = useFetchData(getProjectLogs, [projectId, imageGenerationTimestamp], [projectId, imageGenerationTimestamp]);
    // Sample data with 10 images and 2 videos for testing


    const activitiesData = response?.map(activity => {
        return {
            id: activity?.bountyId,
            logType: activity?.logType,
            type: activityTypeProjectLog[activity?.logType],
            title: activity?.bountyTitle,
            description: activity?.bountyDescription,
            date: activity?.timestamp,
            acceptanceCount: activity?.acceptanceCount,
            completedCount: activity?.completionCount,
            impactCertificatesIssued: activity?.impactCertificatesIssued,
            rewardedAmountUSD: activity?.rewardedAmountUSD,
            submissionCount: activity?.submissionCount,
            submissionData: activity?.submissionData,
            validationType: activity?.validationType,
            impactPointsRewarded: activity?.impactPointsRewarded
        }
    }
    )
    // [
    //     {
    //         id: 1,
    //         type: 'Activity creation',
    //         title: "'Setup community solar panels' created",
    //         description: "The activity is about planning and executing on Gitcoin missions on Radio during GG20",
    //         date: '2024-02-01T00:00:00Z',
    //         media: []
    //     },
    //     {
    //         id: 2,
    //         type: 'Activity validation',
    //         title: "Funds worth $500 deployed",
    //         description: "80 submissions were validated, 40 Impact certificates were awarded & 4 claims rewarded",
    //         date: '2024-01-29T00:00:00Z',
    //         media: [
    //             // 2 Videos
    //             {
    //                 type: 'video',
    //                 url: 'https://picsum.photos/400/300?random=1',
    //                 thumbnail: 'https://picsum.photos/400/300?random=1',
    //                 username: 'changemaker001',
    //                 impactPoints: '3000',
    //                 caption: 'Taken at 12:00 pm GMT +5:30, 22 Mar 2025, Indirangar Be...'
    //             },
    //             {
    //                 type: 'video',
    //                 url: 'https://picsum.photos/400/300?random=2',
    //                 thumbnail: 'https://picsum.photos/400/300?random=2',
    //                 username: 'eco_warrior',
    //                 impactPoints: '2500',
    //                 caption: 'Community garden maintenance session'
    //             },
    //             // 10 Images
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=3',
    //                 username: 'tinawest12',
    //                 impactPoints: '2000',
    //                 caption: 'Community garden work'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=4',
    //                 username: 'avengers2554',
    //                 impactPoints: '1000',
    //                 caption: 'Water conservation project'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=5',
    //                 username: 'green_thumb',
    //                 impactPoints: '1500',
    //                 caption: 'Solar panel installation progress'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=6',
    //                 username: 'nature_lover',
    //                 impactPoints: '1800',
    //                 caption: 'Tree planting initiative'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=7',
    //                 username: 'sustainability_expert',
    //                 impactPoints: '2200',
    //                 caption: 'Recycling workshop'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=8',
    //                 username: 'climate_activist',
    //                 impactPoints: '1200',
    //                 caption: 'Clean energy demonstration'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=9',
    //                 username: 'eco_volunteer',
    //                 impactPoints: '1600',
    //                 caption: 'Beach cleanup activity'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=10',
    //                 username: 'green_community',
    //                 impactPoints: '1900',
    //                 caption: 'Urban farming project'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=11',
    //                 username: 'solar_enthusiast',
    //                 impactPoints: '2100',
    //                 caption: 'Renewable energy workshop'
    //             },
    //             {
    //                 type: 'image',
    //                 url: 'https://picsum.photos/400/300?random=12',
    //                 username: 'earth_protector',
    //                 impactPoints: '1400',
    //                 caption: 'Wildlife conservation effort'
    //             }
    //         ]
    //     }
    // ];

    // Use sample data if no activities provided

    const refineActivitiesData = activitiesData?.filter((activity) => {
        if (activity?.logType === "BOUNTY_EXPIRATION") {
            return activity?.acceptanceCount !== 0;
        }
        else if (activity?.logType === "BOUNTY_VALIDATION") {
            return activity?.impactCertificatesIssued !== 0 || activity?.impactPointsRewarded !== 0 || activity?.rewardedAmountUSD !== 0
        }

        return true
    });
    console.log("refineActivitiesData", refineActivitiesData);
    if (loading) return <div className='w-full h-[23.9375rem] flex pt-11 justify-center '  ><Loader /></div>;
    console.log("Project Activity Log:", { loading, response, error }, "ye render karna h ");
    if (refineActivitiesData?.length === 0) return <div className="w-full h-[23.9375rem] flex items-center justify-center text-gray-500">No data available</div>;



    if (!loading && refineActivitiesData?.length > 0)
        return (
            <div className="w-full">
                {/* Title */}
                <h2 className="text-xl font-bold text-textSecondary mb-2">Project Activity Log</h2>

                {/* Timeline Container */}
                <div className="relative w-full">
                    {/* Vertical Timeline Line - Solid line instead of dashed */}
                    <div className="absolute left-2 top-0 bottom-0 w-[.3125rem] bg-borderUnderline"></div>

                    {/* Activity Entries */}
                    <div className="space-y-4 w-full">
                        {refineActivitiesData.map((activity, index) => {
                            console.log("activity condition aayegi", activity);

                            return <ActivityEntry key={activity.title + index} activity={activity} />
                        }
                        )}
                    </div>
                </div>
            </div>
        );
};

const ActivityEntry = ({ activity }) => {
    const { type, date, logType } = activity;

    return (
        <div className="relative w-full ">
            {/* Timeline Dot - Solid circular marker */}
            <div className="absolute left-[.625rem] -translate-x-1/2 top-6 w-4 h-4 bg-textSecondary rounded-full shadow-sm"></div>

            {/* Activity Content */}
            <div className="ml-8 w-[calc(100%-2rem)] ">
                <div className="rounded-md p-4 w-full bg-grayBgNormal shadow-custom-multi-small">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">

                            <span className={`px-2 py-1 text-xs font-semibold rounded-[.25rem] border  border-border-underline bg-tagGray200Bg text-tagGray200Text`}>
                                {type}
                            </span>

                        </div>
                        <span className="text-xs text-textSecondary font-semibold">
                            {date ? formatIntDateAndThenTime(date) : 'N/A'}
                        </span>
                    </div>
                    {
                        logType === "BOUNTY_CREATION" && <ActivityCreation activity={activity} />
                    }
                    {
                        logType === "BOUNTY_EXPIRATION" && <ActivityParticipation activity={activity} />
                    }
                    {
                        logType === "BOUNTY_VALIDATION" && <ActivityValidation activity={activity} />
                    }
                </div>
            </div>
        </div>
    );
};


const ActivityCreation = ({ activity }) => {
    const { title, description, media } = activity;
    return (
        <div>
            {/* Title */}
            <h3 className="text-lg leading-6 font-medium text-textPrimary mb-2">
                ‘{title}’ created
            </h3>

            {/* Description */}

            <BountyDescription htmlClassName="prose  text-textPrimary text-xs leading-5 max-w-none [&_p:empty]:before:content-['\\00a0'] [&_p:empty]:inline-block" paragraphClassName='text-textPrimary text-xs' html={description} />

            {/* Media Content */}
            {media && media.length > 0 && (
                <div className="w-full mt-2">
                    <MediaGallery
                        mediaItems={[...media, ...media]} // Duplicate for testing
                        title={title}
                        showScrollArrows={true}
                        containerId={`activity-${activity.id || 'default'}`}
                    />
                </div>
            )}
        </div>
    )
}

const ActivityParticipation = ({ activity }) => {

    const { title, acceptanceCount, completedCount } = activity;

    return (
        <div>
            {/* Title */}
            <h3 className="text-lg leading-6 font-medium text-textPrimary mb-2">
                {acceptanceCount} {acceptanceCount > 1 ? "people" : "person"}  participated in activity ‘{title}’
            </h3>

            {/* Description */}
            <p className="text-xs leading-5 text-textPrimary ">
                {acceptanceCount} {acceptanceCount > 1 ? "participants" : "participant"} started the activity and {completedCount} of them completed it
            </p>

        </div>
    )
}

function extractUsersWithMedia(users) {
    const allowed = new Set(['UPLOAD_MULTIPLE_PHOTOS', 'UPLOAD_VIDEO', 'UPLOAD_MULTIPLE_VIDEO']);

    return (users || [])
        .map(user => {
            const filtered = (user.uploadedFiles || []).filter(f => allowed.has(f.actionType));
            const media = [];
            for (const item of filtered) {
                const isPhotos = item.actionType === 'UPLOAD_MULTIPLE_PHOTOS';
                const isVideo = item.actionType === 'UPLOAD_VIDEO' || item.actionType === 'UPLOAD_MULTIPLE_VIDEO';
                const locations = Array.isArray(item.fileLocation) ? item.fileLocation : [];
                for (const loc of locations) {
                    if (isPhotos) {
                        media.push({
                            type: 'image',
                            url: loc,
                            thumbnail: loc,
                            username: user.username,
                            impactPoints: user.impactPointsRewarded,
                            actionType: item.actionType,
                            actions: item.action || [],
                        });
                    } else if (isVideo) {
                        media.push({
                            type: 'video',
                            url: loc,
                            thumbnail: loc,
                            username: user.username,
                            impactPoints: user.impactPointsRewarded,
                            actionType: item.actionType,
                            actions: item.action || [],
                        });
                    }
                }
            }
            if (media.length > 0) {
                return { ...user, media };
            }
            return null;
        })
        .filter(Boolean);
}

const ActivityValidation = ({ activity }) => {
    const { title, description, media,
        impactCertificatesIssued,
        submissionCount,
        submissionData,
        rewardedAmountUSD,
        impactPointsRewarded
    } = activity;

    const userMedias = extractUsersWithMedia(submissionData) || [];

    // Build dynamic title parts array
    const titleParts = [];

    if (rewardedAmountUSD > 0) {
        titleParts.push(`Funds worth $${formatNumber(rewardedAmountUSD)} deployed`);
    }

    if (impactCertificatesIssued > 0) {
        titleParts.push(`${impactCertificatesIssued} ${impactCertificatesIssued > 1 ? "Impact Certificates" : "Impact Certificate"} issued`);
    }

    if (impactPointsRewarded > 0) {
        titleParts.push(`${formatNumber(impactPointsRewarded)} Impact Points allocated`);
    }

    // Join parts with commas and ampersand
    const titleText = titleParts.length > 0
        ? titleParts.length === 1
            ? `${titleParts[0]} for activity '${title}'`
            : `${titleParts.slice(0, -1).join(', ')} & ${titleParts[titleParts.length - 1]} for activity '${title}'`
        : '';

    return (
        <div>
            {/* Title */}
            {titleText && (
                <h3 className="text-lg leading-6 font-medium text-textPrimary mb-2">
                    {titleText}
                </h3>
            )}
            {/* Description */}
            <p className="text-xs leading-5 text-textPrimary ">
                {submissionCount} {submissionCount > 1 ? "submissions were" : "submission was"} validated
            </p>

            {/* Users horizontal scroller */}
            <UserMediaRow title={title} userMedias={userMedias} containerId={activity.id || 'default'} />
        </div>
    )
}

export default ProjectActivityLog;